import FilterValueRendererRouter from "../FilterValueRendererRouter";
import { normalizeToRequestLogStatus } from "../config/filter-value-type-behavior";
import { ValueDisplayType, type FilterDefinition } from "../config/structured-search-schema";

import { STATUS_CHIP_LABEL } from "./StatusValueLabel";

import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import { cn } from "@/lib/utils";

type MultiValueBadgesLayout = "fill" | "inline";

interface MultiValueBadgesProps {
  values: string[];
  displayValues?: string[];
  definition?: FilterDefinition;
  onRemove?: (value: string) => void;
  badgeClassName?: string;
  textClassName?: string;
  layout?: MultiValueBadgesLayout;
  maxVisibleItems?: number;
  overflowIndicatorWidth?: number;
}

const getBadgeLabel = (value: string) => {
  const requestStatus = normalizeToRequestLogStatus(value);
  if (requestStatus) {
    return STATUS_CHIP_LABEL[requestStatus];
  }

  return STATUS_CHIP_LABEL[value as keyof typeof STATUS_CHIP_LABEL] ?? value;
};

const OverflowBadge = ({
  hiddenCount,
  hiddenLabels,
  badgeClassName,
}: {
  hiddenCount: number;
  hiddenLabels: string;
  badgeClassName?: string;
}) => (
  <BasicTooltip content={hiddenLabels} shouldShow={hiddenLabels.length > 0}>
    <span className="pointer-events-auto inline-flex shrink-0">
      <Badge
        variant={BadgeVariant.BLUE}
        size={BadgeSize.SM}
        className={cn("pointer-events-auto shrink-0", badgeClassName)}
      >
        +{hiddenCount}
      </Badge>
    </span>
  </BasicTooltip>
);

const renderBadge = ({
  value,
  displayValue,
  definition,
  badgeClassName,
  textClassName,
  onRemove,
}: {
  value: string;
  displayValue?: string;
  definition?: FilterDefinition;
  badgeClassName?: string;
  textClassName?: string;
  onRemove?: () => void;
}) => {
  const presentation = definition?.valueDisplay ?? ValueDisplayType.Default;

  if (presentation !== ValueDisplayType.Default) {
    return (
      <FilterValueRendererRouter
        key={value}
        definition={definition}
        value={value}
        label={displayValue}
        className={badgeClassName}
        onRemove={onRemove}
      />
    );
  }

  const badgeLabel = getBadgeLabel(displayValue ?? value);

  return (
    <Badge
      key={value}
      variant={BadgeVariant.BLUE}
      size={BadgeSize.SM}
      className={cn("min-w-0 shrink", badgeClassName)}
      onRemove={onRemove}
    >
      <OverflowDisplayTooltip content={badgeLabel}>
        <span className={textClassName ?? "max-w-[160px] truncate"}>{badgeLabel}</span>
      </OverflowDisplayTooltip>
    </Badge>
  );
};

const MultiValueBadges = ({
  values,
  displayValues,
  definition,
  onRemove,
  badgeClassName,
  textClassName,
  layout = "fill",
  maxVisibleItems,
  overflowIndicatorWidth = 40,
}: MultiValueBadgesProps) => {
  const isInlineLayout = layout === "inline";
  const recalcKey = `${layout}|${maxVisibleItems ?? ""}|${overflowIndicatorWidth}|${definition?.key ?? ""}|${values.join("|")}|${String(Boolean(onRemove))}|${badgeClassName ?? ""}|${textClassName ?? ""}`;
  const measurementRemoveHandler = onRemove ? () => undefined : undefined;
  const { containerRef, measureRef, visibleCount, hiddenCount } = useTruncatedList({
    itemCount: values.length,
    keepMinOne: true,
    overflowIndicatorWidth,
    maxVisibleItems,
    recalcKey,
  });
  const hiddenLabels = values
    .slice(visibleCount)
    .map((value, index) => getBadgeLabel(displayValues?.[visibleCount + index] ?? value))
    .join(", ");

  if (values.length === 0) {
    return null;
  }

  const containerClassName =
    "relative flex w-full min-w-0 max-w-full flex-nowrap items-center gap-1 overflow-hidden";
  const measureClassName = isInlineLayout
    ? "pointer-events-none invisible absolute flex w-full min-w-0 max-w-full flex-nowrap items-center gap-1"
    : "pointer-events-none invisible absolute flex w-full flex-nowrap items-center gap-1";

  if (visibleCount === 0 && hiddenCount > 0) {
    return (
      <div ref={containerRef} className={containerClassName}>
        <div ref={measureRef} className={measureClassName} aria-hidden="true">
          {values.map((value, index) =>
            renderBadge({
              value,
              displayValue: displayValues?.[index],
              definition,
              badgeClassName,
              textClassName,
              onRemove: measurementRemoveHandler,
            }),
          )}
        </div>
        <OverflowBadge
          hiddenCount={hiddenCount}
          hiddenLabels={hiddenLabels}
          badgeClassName={badgeClassName}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={containerClassName}>
      <div ref={measureRef} className={measureClassName} aria-hidden="true">
        {values.map((value, index) =>
          renderBadge({
            value,
            displayValue: displayValues?.[index],
            definition,
            badgeClassName,
            textClassName,
            onRemove: measurementRemoveHandler,
          }),
        )}
      </div>
      {values.slice(0, visibleCount).map((value, index) =>
        renderBadge({
          value,
          displayValue: displayValues?.[index],
          definition,
          badgeClassName,
          textClassName,
          onRemove: onRemove ? () => onRemove(value) : undefined,
        }),
      )}
      {hiddenCount > 0 && (
        <OverflowBadge
          hiddenCount={hiddenCount}
          hiddenLabels={hiddenLabels}
          badgeClassName={badgeClassName}
        />
      )}
    </div>
  );
};

export default MultiValueBadges;
